/**
 * Video Handler
 *
 * Orchestrates video selection, thumbnail generation, validation, and state updates.
 */

import { VideoCanvas } from "./video-canvas";
import { ensureRemovedAttachedVideosInput } from "./video-dom-render";
import type { VideoThumbnailData } from "./video-render";
import { videoForm } from "./video-form";

export interface ExistingVideoData {
    filename: string;
    url?: string;
}

interface VideoHandlerConfig {
    maxFiles: number;
    acceptedTypes: string;
    existingVideos: ExistingVideoData[];
}

type ResolveCallback = (value: VideoThumbnailData) => void;
type RejectCallback = (reason?: unknown) => void;

class VideoHandler {
    public postKey: string;
    public mode: string;
    public config: VideoHandlerConfig;
    public selectedVideos: VideoThumbnailData[];
    public removedExistingVideos: string[];
    public container!: HTMLElement;
    public fileInput!: HTMLInputElement;
    public addBtn!: HTMLButtonElement;
    public videoGrid!: HTMLElement;

    public constructor(postKey: string, mode: string, options: Partial<VideoHandlerConfig> = {}) {
        this.postKey = postKey;
        this.mode = mode;

        const defaults: VideoHandlerConfig = {
            maxFiles: 3,
            acceptedTypes: "video/*",
            existingVideos: [],
        };

        this.config = { ...defaults, ...options };
        this.selectedVideos = [];
        this.removedExistingVideos = [];
    }

    public updateFormInput(): void {
        if (this.mode !== "edit") return;

        const input = ensureRemovedAttachedVideosInput(this.container);

        if (input) {
            input.value = JSON.stringify(this.removedExistingVideos);
        }
    }

    public static handleMetadataLoaded(video: HTMLVideoElement): void {
        const seekTime = isFinite(video.duration) && video.duration > 0 ? Math.min(1, video.duration * 0.1) : 0;
        video.currentTime = seekTime;
    }

    public static handleSeeked(
        video: HTMLVideoElement,
        file: File,
        url: string,
        resolve: ResolveCallback,
        reject: RejectCallback,
    ): void {
        try {
            const thumbnailDataUrl = VideoCanvas.drawVideoFrame(video);

            const videoData: VideoThumbnailData = {
                id: Date.now() + Math.random(),
                file,
                name: file.name,
                size: file.size,
                duration: video.duration,
                previewUrl: thumbnailDataUrl,
                videoUrl: url,
                url,
            };

            resolve(videoData);
        } catch (error) {
            reject(error);
        }
    }

    public static handleError(url: string, reject: RejectCallback, error: Event): void {
        URL.revokeObjectURL(url);
        reject(error);
    }

    public static handleExistingVideoRemoval(handler: VideoHandler, event: Event): void {
        event.preventDefault();
        const target = videoForm.getEventTarget(event);
        const removeBtn = videoForm.getClosestRemoveButton(target);
        if (!removeBtn) return;

        const tile = videoForm.getExistingVideoTile(removeBtn);
        if (!tile) return;

        const filename = tile.dataset.videoFilename;
        if (!filename) return;

        const isRemoved = tile.dataset.removed === "true";
        if (isRemoved) {
            tile.dataset.removed = "false";
            tile.style.opacity = "1";
            const removedIndicator = videoForm.getRemovedIndicator(tile);
            if (removedIndicator) removedIndicator.style.display = "none";
            const controls = videoForm.getMediaControls(tile);
            if (controls) controls.style.display = "";

            const index = handler.removedExistingVideos.indexOf(filename);
            if (index > -1) {
                handler.removedExistingVideos.splice(index, 1);
            }
        } else {
            tile.dataset.removed = "true";
            tile.style.opacity = "0.5";
            const removedIndicator = videoForm.getRemovedIndicator(tile);
            if (removedIndicator) removedIndicator.style.display = "flex";
            const controls = videoForm.getMediaControls(tile);
            if (controls) controls.style.display = "none";

            if (!handler.removedExistingVideos.includes(filename)) {
                handler.removedExistingVideos.push(filename);
            }
        }

        handler.updateFormInput();
    }
}

export { VideoHandler };
